import { useId } from "react";

/**
 * Inline-SVG brand marks (sources: src/assets/compaire-{lockup,wordmark,icon}.svg).
 *
 * The COMPAIRE letterforms render with `currentColor` and default to teal deep
 * (#0E4347) via the SVG `color` presentation attribute, so a text color class
 * (e.g. `text-white`) or a `color`/`style` prop re-tints them for dark surfaces.
 * The gold "PAIR" letters are fixed #FFB703 — the wordmark stays two-toned in
 * every context. The icon mark is intentionally multi-color and never adapts.
 */

type MarkProps = React.SVGProps<SVGSVGElement>;

const TEAL_DEEP = "#0E4347";
const GOLD = "#FFB703";

/**
 * Letterform paths shared by the wordmark and lockup, in drawn coordinates.
 * The last path is the drawn ™. At small sizes (the 18px sidebar mark) it is
 * about 3px tall with sub-pixel strokes and renders as a smudge, so callers
 * that small pass `trademark={false}` and set ™ as text instead.
 */
const WordmarkArt = ({ trademark = true }: { trademark?: boolean }) => (
  <>
    <path fill="currentColor" d="M1950 3413l-29 -28c-60,58 -118,91 -202,91 -140,0 -248,-116 -248,-264l0 -2c0,-147 106,-262 247,-262 88,0 147,37 198,86l31 -32c-61,-55 -123,-93 -228,-93 -169,0 -293,137 -293,303l0 2c0,168 124,301 291,301 104,0 171,-41 233,-102z" />
    <path fill="currentColor" d="M2728 3212c1,-1 1,-1 0,-2 0,-160 -116,-301 -294,-301 -177,0 -296,143 -296,303l0 2c0,160 117,301 295,301 177,0 295,-143 295,-303zm-45 2c0,145 -104,262 -249,262 -145,0 -250,-119 -250,-264l0 -2c0,-145 104,-262 249,-262 145,0 250,119 250,264l0 2z" />
    <polygon fill="currentColor" points="3541,3505 3541,2919 3500,2919 3265,3266 3029,2919 2988,2919 2988,3505 3030,3505 3030,2996 3262,3333 3265,3333 3497,2996 3497,3505 " />
    <polygon fill={GOLD} points="5263,3505 5191,3505 5191,2923 5263,2923 " />
    <path fill={GOLD} d="M4703 3017c-108,237 -128,280 -223,488l-77 0 271 -586 59 0 272 586 -80 0c-94,-206 -116,-253 -222,-488z" />
    <path fill={GOLD} d="M3881 2985l0 246 140 0c53,0 114,-16 144,-64 11,-18 16,-39 16,-60l0 -1c0,-91 -77,-121 -155,-121l-145 0zm0 312l0 208 -69 0 0 -586 217 0c69,0 144,20 187,77 24,31 34,69 34,107l0 2c0,130 -114,192 -232,192l-137 0z" />
    <path fill={GOLD} d="M5613 2985l0 224 174 0c48,0 103,-14 132,-55 12,-18 17,-38 17,-59l0 -2c0,-19 -4,-38 -14,-54 -27,-43 -85,-54 -132,-54l-177 0zm162 289l-162 0 0 231 -70 0 0 -586 249 0c74,0 155,25 193,93 14,25 20,52 20,79l0 2c0,94 -65,151 -152,169l187 243 -87 0 -178 -231z" />
    <polygon fill="currentColor" points="6314,2959 6688,2959 6688,2919 6271,2919 6271,2959 " />
    <polygon fill="currentColor" points="6692,3505 6692,3465 6314,3465 6314,3230 6650,3230 6650,3189 6314,3189 6271,3189 6271,3505 " />
    {trademark && <path fill="currentColor" d="M6834 3027l0 -104 -39 0 0 -14 94 0 0 14 -39 0 0 104 -16 0zm94 0l0 -118 24 0 28 84c2,7 4,13 5,17 2,-4 4,-10 7,-19l28 -82 21 0 0 118 -15 0 0 -99 -35 99 -14 0 -34 -101 0 101 -15 0z" />}
  </>
);

/** Dot-grid icon mark in its native 0–100 coordinate space. Fixed colors by design. */
const IconArt = ({ gradientId }: { gradientId: string }) => (
  <>
    <circle cx="22" cy="32" r="3" fill="#001B4D" fillOpacity="0.32" />
    <circle cx="22" cy="50" r="5.5" fill={TEAL_DEEP} />
    <circle cx="22" cy="68" r="3" fill="#001B4D" fillOpacity="0.32" />
    <circle cx="22" cy="86" r="3" fill="#001B4D" fillOpacity="0.224" />
    <circle cx="78" cy="14" r="3" fill="#001B4D" fillOpacity="0.224" />
    <circle cx="78" cy="32" r="3" fill="#001B4D" fillOpacity="0.32" />
    <circle cx="78" cy="50" r="5.5" fill={TEAL_DEEP} />
    <circle cx="78" cy="68" r="3" fill="#001B4D" fillOpacity="0.32" />
    <path d="M 28 50 L 72 50" stroke={`url(#${gradientId})`} strokeWidth="2.8" fill="none" strokeLinecap="round" />
    <circle cx="50" cy="50" r="4.5" fill={GOLD} />
    <circle cx="50" cy="50" r="9" fill="none" stroke={GOLD} strokeOpacity="0.35" strokeWidth="1" />
  </>
);

const FlowGradient = ({ id }: { id: string }) => (
  <linearGradient id={id} x1="0" x2="1">
    <stop offset="0" stopColor="#06B6D4" />
    <stop offset="1" stopColor={GOLD} />
  </linearGradient>
);

/** url(#…) fragments choke on the colons React's useId emits, so strip them. */
const useGradientId = () => {
  const uid = useId();
  return `compaire-flow-${uid.replace(/[^a-zA-Z0-9_-]/g, "")}`;
};

/** Full lockup: dot-grid icon mark + two-tone COMPAIRE wordmark. */
export const CompaireLockup = (props: MarkProps) => {
  const gradientId = useGradientId();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="1426 697 6500 606"
      color={TEAL_DEEP}
      role="img"
      aria-label="Compaire"
      {...props}
    >
      <defs>
        <FlowGradient id={gradientId} />
      </defs>
      <g transform="translate(869.843567 -2212)">
        <WordmarkArt />
        <g transform="matrix(3.3868041 0 0 3.3868041 341.20849609 2801.05908203) translate(68.46534653465346 20) scale(2)">
          <IconArt gradientId={gradientId} />
        </g>
      </g>
    </svg>
  );
};

/** Two-tone COMPAIRE wordmark without the icon mark. */
export const CompaireWordmark = ({ trademark = true, ...props }: MarkProps & { trademark?: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    // Without the drawn ™ the box ends just past the final E.
    viewBox={trademark ? "1426 697 5650 606" : "1426 697 5300 606"}
    color={TEAL_DEEP}
    role="img"
    aria-label="Compaire"
    {...props}
  >
    <g transform="translate(17.5 -2212)">
      <WordmarkArt trademark={trademark} />
    </g>
  </svg>
);

/** Dot-grid icon mark only. */
export const CompaireIcon = (props: MarkProps) => {
  const gradientId = useGradientId();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      role="img"
      aria-label="Compaire"
      {...props}
    >
      <defs>
        <FlowGradient id={gradientId} />
      </defs>
      <IconArt gradientId={gradientId} />
    </svg>
  );
};
